import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { AppFrame, type AppFrameProps } from './app-frame';

/**
 * A view's inspector (`page.inspector.open`, the extensions proposal's "Inspecteur"): a route of
 * the view, shown by the container that frames it in the frame's 320 px column. The container
 * holds what is open there; the view reads it as `context.inspector`.
 */
export type ViewInspector = {
  open: { route: string; title?: string | undefined } | null;
  show(route: string, title: string | undefined): void;
  close(): void;
};

export function useViewInspector(): ViewInspector {
  const [open, setOpen] = useState<ViewInspector['open']>(null);
  const show = useCallback(
    (route: string, title: string | undefined) =>
      setOpen((current) =>
        current?.route === route && current.title === title ? current : { route, title },
      ),
    [],
  );
  const close = useCallback(() => setOpen(null), []);
  return useMemo(() => ({ open, show, close }), [open, show, close]);
}

/** The view's handlers with its inspector's: `context.inspector`, open and close. */
export function withInspector(inspector: ViewInspector, view: AppFrameProps): AppFrameProps {
  return {
    ...view,
    inspector: inspector.open?.route ?? null,
    handlers: {
      ...view.handlers,
      openInspector: inspector.show,
      closeInspector: inspector.close,
    },
  };
}

/**
 * The inspector's document: the same view on the inspector's route (`context.layout`
 * `inspector`), without the bar's actions, sections or levels, which stay the view's.
 */
export function inspectorView(inspector: ViewInspector, view: AppFrameProps): ReactNode {
  if (inspector.open === null) return null;
  const {
    setSections: _sections,
    setHeader: _header,
    navigate: _navigate,
    back: _back,
    ...handlers
  } = view.handlers;
  return (
    <AppFrame
      {...view}
      action={null}
      fragment={inspector.open.route}
      handlers={{ ...handlers, openInspector: inspector.show, closeInspector: inspector.close }}
      inspector={inspector.open.route}
      layout="inspector"
      route={inspector.open.route}
      title={inspector.open.title ?? view.title}
    />
  );
}
